import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import {
  applyBrightSitesInventorySync,
  syncBrightSitesInventory,
} from '@/services/warehouse'
import { isBrightSitesAuthError } from '@/services/brightsitesExtension'
import type { BrightSitesInventorySyncResponse } from '@/services/warehouseTypes'
import { toast } from '@/store/toastStore'
import { formatApiError } from '@/utils/apiError'
import {
  buildInitialCatalogState,
  catalogCanApply,
  catalogRowKey,
  catalogSku,
  catalogTypeLabel,
  defaultCatalogAccepted,
  defaultProposedName,
  type CatalogRowState,
} from '@/components/warehouseBrightSitesSyncHelpers'

const STICKY_HEADER_STYLE = {
  position: 'sticky' as const,
  top: 0,
  zIndex: 10,
  backgroundColor: 'var(--table-header-bg)',
}

type WarehouseBrightSitesCatalogSyncOptions = {
  busy?: boolean
  authRecoveryHint?: boolean
  onAuthError?: () => void
  onApplied?: () => void | Promise<void>
}

type CatalogSyncContextValue = WarehouseBrightSitesCatalogSyncOptions & {
  syncing: boolean
  applying: boolean
  syncResult: BrightSitesInventorySyncResponse | null
  catalogRows: BrightSitesInventorySyncResponse['catalog']
  catalogState: Record<string, CatalogRowState>
  setCatalogState: React.Dispatch<React.SetStateAction<Record<string, CatalogRowState>>>
  cardBusy: boolean
  hasCatalogRows: boolean
  hasActionableCatalogRows: boolean
  actionableCatalogRows: BrightSitesInventorySyncResponse['catalog']
  acceptedCatalogCount: number
  canApply: boolean
  handleSync: () => Promise<void>
  handleApply: () => Promise<void>
}

const CatalogSyncContext = createContext<CatalogSyncContextValue | null>(null)

function useCatalogSyncContext(): CatalogSyncContextValue {
  const context = useContext(CatalogSyncContext)
  if (!context) {
    throw new Error('WarehouseBrightSitesCatalogSync components must be used within a provider')
  }
  return context
}

export function WarehouseBrightSitesCatalogSyncProvider({
  busy = false,
  authRecoveryHint = false,
  onAuthError,
  onApplied,
  children,
}: WarehouseBrightSitesCatalogSyncOptions & { children: ReactNode }) {
  const [syncing, setSyncing] = useState(false)
  const [applying, setApplying] = useState(false)
  const [syncResult, setSyncResult] = useState<BrightSitesInventorySyncResponse | null>(null)
  const [catalogState, setCatalogState] = useState<Record<string, CatalogRowState>>({})

  const catalogRows = syncResult?.catalog ?? []
  const hasCatalogRows = catalogRows.length > 0
  const actionableCatalogRows = useMemo(
    () => catalogRows.filter(catalogCanApply),
    [catalogRows]
  )
  const hasActionableCatalogRows = actionableCatalogRows.length > 0
  const cardBusy = busy || syncing || applying

  const acceptedCatalogCount = useMemo(
    () =>
      catalogRows.filter(
        (row, index) =>
          catalogCanApply(row) && catalogState[catalogRowKey(row, index)]?.accepted
      ).length,
    [catalogRows, catalogState]
  )

  const canApply = hasCatalogRows && acceptedCatalogCount > 0

  async function handleSync() {
    setSyncing(true)
    try {
      const result = await syncBrightSitesInventory()
      setSyncResult(result)
      setCatalogState(buildInitialCatalogState(result.catalog))
      toast.success(result.message)
      for (const warning of result.warnings ?? []) {
        toast.warning(warning)
      }
    } catch (err) {
      toast.error(formatApiError(err))
      if (isBrightSitesAuthError(err)) onAuthError?.()
    } finally {
      setSyncing(false)
    }
  }

  async function handleApply() {
    if (!syncResult) return
    setApplying(true)
    try {
      const appliedRowKeys = new Set<string>()

      const catalog_adds = catalogRows
        .map((row, index) => ({ row, key: catalogRowKey(row, index) }))
        .filter(
          ({ row, key }) =>
            row.proposal_type === 'missing_in_catalog' && catalogState[key]?.accepted
        )
        .map(({ row, key }) => ({
          key,
          full_sku: row.full_sku ?? '',
          bk_sku: row.bk_sku ?? row.full_sku ?? '',
          display_name:
            catalogState[key]?.proposedName.trim() ||
            defaultProposedName(row) ||
            row.full_sku ||
            '',
          wh_sku: row.wh_sku,
          bright_catalog_sku: row.bright_catalog_sku,
        }))
        .filter((row) => row.full_sku && row.bk_sku && row.display_name)
        .map(({ key, ...row }) => {
          appliedRowKeys.add(key)
          return row
        })

      const catalog_display_updates = catalogRows
        .map((row, index) => ({ row, key: catalogRowKey(row, index) }))
        .filter(
          ({ row, key }) =>
            row.proposal_type === 'name_mismatch' &&
            row.crosswalk_id != null &&
            catalogState[key]?.accepted
        )
        .map(({ row, key }) => ({
          key,
          crosswalk_id: row.crosswalk_id as number,
          display_name:
            catalogState[key]?.proposedName.trim() || defaultProposedName(row) || '',
        }))
        .filter((row) => row.display_name)
        .map(({ key, ...row }) => {
          appliedRowKeys.add(key)
          return row
        })

      const result = await applyBrightSitesInventorySync({
        catalog_adds,
        catalog_display_updates,
      })

      const appliedCount = result.catalog_added + result.catalog_updated
      if (appliedCount > 0) {
        const parts: string[] = []
        if (result.catalog_added > 0) parts.push(`${result.catalog_added} catalog add(s)`)
        if (result.catalog_updated > 0) parts.push(`${result.catalog_updated} name update(s)`)
        toast.success(`Applied ${parts.join(', ')}.`)

        const remainingCatalog = syncResult.catalog.filter(
          (row, index) => !appliedRowKeys.has(catalogRowKey(row, index))
        )
        const hasActionableRemaining = remainingCatalog.some(catalogCanApply)

        setSyncResult((current) => {
          if (!current) return current
          return {
            ...current,
            catalog: hasActionableRemaining ? remainingCatalog : [],
          }
        })
        setCatalogState((current) => {
          if (!hasActionableRemaining) return {}
          const next = { ...current }
          for (const key of appliedRowKeys) delete next[key]
          return next
        })
      } else {
        toast.warning(
          'Nothing was applied — check that accepted rows have valid SKU and crosswalk IDs.'
        )
      }

      if (result.warnings?.length) {
        for (const warning of result.warnings) toast.warning(warning)
      }

      await onApplied?.()
    } catch (err) {
      toast.error(formatApiError(err))
      if (isBrightSitesAuthError(err)) onAuthError?.()
    } finally {
      setApplying(false)
    }
  }

  const value: CatalogSyncContextValue = {
    busy,
    authRecoveryHint,
    onAuthError,
    onApplied,
    syncing,
    applying,
    syncResult,
    catalogRows,
    catalogState,
    setCatalogState,
    cardBusy,
    hasCatalogRows,
    hasActionableCatalogRows,
    actionableCatalogRows,
    acceptedCatalogCount,
    canApply,
    handleSync,
    handleApply,
  }

  return <CatalogSyncContext.Provider value={value}>{children}</CatalogSyncContext.Provider>
}

export function WarehouseBrightSitesCatalogSyncActions() {
  const {
    syncing,
    applying,
    syncResult,
    cardBusy,
    canApply,
    handleSync,
    handleApply,
  } = useCatalogSyncContext()

  const lastSyncTooltip = syncResult
    ? `Last sync ${new Date(syncResult.synced_at).toLocaleString()}`
    : 'Never synced'

  return (
    <>
      <button
        type="button"
        className="btn btn-secondary text-sm"
        disabled={cardBusy}
        title={lastSyncTooltip}
        onClick={() => void handleSync()}
      >
        {syncing ? (
          <>
            <ArrowPathIcon className="h-4 w-4 animate-spin" />
            Syncing…
          </>
        ) : (
          'Sync catalog'
        )}
      </button>
      {canApply ? (
        <button
          type="button"
          className="btn btn-primary text-sm"
          disabled={cardBusy}
          onClick={() => void handleApply()}
        >
          {applying ? 'Saving…' : 'Save Changes'}
        </button>
      ) : null}
    </>
  )
}

export function WarehouseBrightSitesCatalogSyncPanel() {
  const {
    authRecoveryHint,
    syncResult,
    catalogRows,
    catalogState,
    setCatalogState,
    cardBusy,
    hasActionableCatalogRows,
  } = useCatalogSyncContext()

  if (!syncResult || !hasActionableCatalogRows) return null

  return (
    <div className="mt-4 shrink-0 space-y-2 rounded-lg border border-amber-200 bg-amber-50/50 p-4">
      <div>
        <h3 className="container-title">Bright Sites catalog differences</h3>
        <p className="mt-1 text-xs text-gray-500">
          Edit proposed names before applying. Rows missing in Bright Sites are review-only.
          Catalog sync never overwrites units per case.
        </p>
        {authRecoveryHint ? (
          <p className="mt-2 text-xs text-amber-700">
            Bright Sites session expired — update credentials on Warehouse Settings, then retry.
          </p>
        ) : null}
      </div>
      <div className="max-h-[24rem] overflow-x-auto overflow-y-auto rounded-lg border border-gray-200 bg-white">
        <table className="table-forecast border-separate border-spacing-0 min-w-full text-sm">
          <thead>
            <tr>
              <th className="w-16" style={STICKY_HEADER_STYLE}>
                Accept
              </th>
              <th style={STICKY_HEADER_STYLE}>Type</th>
              <th style={STICKY_HEADER_STYLE}>SKU</th>
              <th style={STICKY_HEADER_STYLE}>Current name</th>
              <th style={STICKY_HEADER_STYLE}>Proposed name</th>
            </tr>
          </thead>
          <tbody>
            {catalogRows.map((row, index) => {
              const key = catalogRowKey(row, index)
              const state = catalogState[key] ?? {
                accepted: defaultCatalogAccepted(row),
                proposedName: defaultProposedName(row),
              }
              const sku = catalogSku(row)
              const isReviewOnly = row.proposal_type === 'missing_in_brightsites'

              return (
                <tr key={key}>
                  <td>
                    {isReviewOnly ? (
                      <span className="text-xs text-gray-400">—</span>
                    ) : (
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-gray-300"
                        checked={state.accepted}
                        disabled={cardBusy}
                        onChange={(event) =>
                          setCatalogState((current) => {
                            const prev = current[key] ?? {
                              accepted: defaultCatalogAccepted(row),
                              proposedName: defaultProposedName(row),
                            }
                            return {
                              ...current,
                              [key]: { ...prev, accepted: event.target.checked },
                            }
                          })
                        }
                        aria-label={`Accept ${catalogTypeLabel(row.proposal_type)} for ${sku}`}
                      />
                    )}
                  </td>
                  <td>{catalogTypeLabel(row.proposal_type)}</td>
                  <td className="font-mono text-xs">{sku}</td>
                  <td>{row.current_display_name ?? '—'}</td>
                  <td>
                    {isReviewOnly ? (
                      <div>
                        <span>{state.proposedName || '—'}</span>
                        <span className="mt-1 block text-xs text-gray-500">
                          Review only — Bright Sites has no stock for this catalog SKU.
                        </span>
                      </div>
                    ) : (
                      <input
                        type="text"
                        className="w-full min-w-[12rem] rounded-md border border-gray-200 px-2 py-1 text-sm"
                        value={state.proposedName}
                        disabled={cardBusy}
                        onChange={(event) =>
                          setCatalogState((current) => {
                            const prev = current[key] ?? {
                              accepted: defaultCatalogAccepted(row),
                              proposedName: defaultProposedName(row),
                            }
                            return {
                              ...current,
                              [key]: { ...prev, proposedName: event.target.value },
                            }
                          })
                        }
                        aria-label={`Proposed name for ${sku}`}
                      />
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

